import React, { useState } from 'react';
import { Layers, ScreenShare, Printer, MonitorCheck, Smartphone, Tablet, Eye } from 'lucide-react';

/**
 * Компонент предпросмотра текста с выбранными параметрами
 */
const TextPreview = ({ formData, results }) => {
  const [previewMode, setPreviewMode] = useState('desktop');
  const [previewContext, setPreviewContext] = useState('normal');

  if (!results) return null;

  // Получаем базовые параметры стиля из результатов
  const baseStyle = {
    fontFamily: formData.fontFamily,
    fontWeight: formData.fontWeight,
    lineHeight: formData.lineHeight,
    letterSpacing: `${formData.letterSpacing}em`,
    textAlign: formData.textAlign,
    color: formData.colorScheme === 'dark' ? '#ffffff' : '#333333',
    backgroundColor: formData.colorScheme === 'dark' ? '#333333' : '#ffffff',
  };

  // Определяем размер шрифта в зависимости от режима предпросмотра
  const getFontSize = () => {
    if (previewMode === 'desktop') {
      return `${results.fontSizes.px}px`;
    } else if (previewMode === 'tablet') {
      return `${parseFloat(results.fontSizes.px) * 0.9}px`;
    } else if (previewMode === 'mobile') {
      return `${parseFloat(results.fontSizes.px) * 0.85}px`;
    } else {
      return `${results.fontSizes.px}px`;
    }
  };

  // Определяем ширину контейнера в зависимости от режима предпросмотра
  const getContainerWidth = () => {
    if (previewMode === 'desktop') {
      return { maxWidth: '100%', width: '100%' };
    } else if (previewMode === 'tablet') {
      return { maxWidth: '768px', width: '100%' };
    } else if (previewMode === 'mobile') {
      return { maxWidth: '375px', width: '100%' };
    } else {
      return { maxWidth: '100%', width: '100%' };
    }
  };

  // Получаем стиль для текста в зависимости от контекста предпросмотра
  const getContextStyle = () => {
    if (previewContext === 'article') {
      return {
        maxWidth: '700px',
        margin: '0 auto',
        padding: '1.5rem'
      };
    } else if (previewContext === 'ui') {
      return {
        padding: '0.75rem',
        borderRadius: '0.5rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      };
    } else if (previewContext === 'print') {
      return {
        maxWidth: '210mm',
        margin: '0 auto',
        padding: '1.5rem',
        border: '1px solid #e5e7eb'
      };
    } else {
      return {
        padding: '1rem'
      };
    }
  };

  // Определяем контент для предпросмотра
  const getPreviewContent = () => {
    // Базовый контент - то, что ввел пользователь
    const userText = formData.textContent || 'Пример текста для проверки размера и читабельности.';
    
    if (previewContext === 'article') {
      return (
        <div>
          <h2 style={{ 
            fontSize: `calc(${getFontSize()} * 1.6)`, 
            marginBottom: '1rem',
            fontWeight: 700,
            ...baseStyle 
          }}>
            Пример статьи
          </h2>
          <p style={{ 
            fontSize: getFontSize(), 
            marginBottom: '1rem',
            ...baseStyle 
          }}>
            {userText}
          </p>
          <p style={{ 
            fontSize: getFontSize(), 
            marginBottom: '1rem',
            ...baseStyle 
          }}>
            Различные элементы и блоки текста могут иметь разное оформление,
            но основной размер текста должен обеспечивать комфортное чтение.
            Оптимальный размер шрифта зависит от многих факторов, включая шрифт,
            расстояние просмотра и целевую аудиторию.
          </p>
          <h3 style={{ 
            fontSize: `calc(${getFontSize()} * 1.3)`, 
            marginTop: '1.5rem',
            marginBottom: '0.75rem',
            fontWeight: 600,
            ...baseStyle 
          }}>
            Подзаголовок
          </h3>
          <p style={{ 
            fontSize: getFontSize(), 
            ...baseStyle 
          }}>
            Расстояние просмотра особенно важно учитывать при выборе размера шрифта.
            Чем больше расстояние между глазами пользователя и экраном или
            листом бумаги, тем крупнее должен быть шрифт.
          </p>
        </div>
      );
    } else if (previewContext === 'ui') {
      return (
        <div>
          <div style={{ 
            fontSize: `calc(${getFontSize()} * 0.9)`, 
            marginBottom: '1rem',
            padding: '0.75rem',
            borderRadius: '0.375rem',
            backgroundColor: formData.colorScheme === 'dark' ? '#3b3b3b' : '#f3f4f6',
            ...baseStyle 
          }}>
            <strong>Интерфейсные элементы</strong>: {userText.substring(0, 50)}...
          </div>
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '1rem'
          }}>
            <button style={{ 
              fontSize: `calc(${getFontSize()} * 0.9)`,
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              backgroundColor: '#4f46e5',
              color: '#ffffff',
              border: 'none',
              cursor: 'pointer'
            }}>
              Кнопка
            </button>
            <span style={{ 
              fontSize: getFontSize(),
              ...baseStyle 
            }}>
              Текст рядом с кнопкой
            </span>
          </div>
          <div style={{ 
            fontSize: getFontSize(), 
            padding: '0.75rem',
            borderRadius: '0.375rem',
            border: `1px solid ${formData.colorScheme === 'dark' ? '#4b5563' : '#d1d5db'}`,
            ...baseStyle 
          }}>
            {userText}
          </div>
        </div>
      );
    } else if (previewContext === 'print') {
      return (
        <div style={{ 
          fontFamily: formData.fontFamily === 'Inter, system-ui, sans-serif' ? '"Times New Roman", serif' : formData.fontFamily 
        }}>
          <h2 style={{ 
            fontSize: `calc(${getFontSize()} * 1.4)`, 
            marginBottom: '1rem',
            fontWeight: 700,
            ...baseStyle,
            color: '#000000',
            backgroundColor: '#ffffff' 
          }}>
            Печатный документ
          </h2>
          <p style={{ 
            fontSize: getFontSize(), 
            marginBottom: '1rem',
            ...baseStyle,
            color: '#000000',
            backgroundColor: '#ffffff'
          }}>
            {userText}
          </p>
          <p style={{ 
            fontSize: getFontSize(), 
            ...baseStyle,
            color: '#000000',
            backgroundColor: '#ffffff'
          }}>
            При выборе размера шрифта для печатных материалов учитывайте требования 
            ГОСТ Р 58511-2019, которые регламентируют минимальные размеры шрифта для различных
            типов изданий. Для взрослой аудитории минимальный кегль обычно составляет 9-10 пунктов.
          </p>
        </div>
      );
    } else {
      // Обычный просмотр
      return (
        <div style={{ 
          fontSize: getFontSize(), 
          ...baseStyle 
        }}>
          {userText}
        </div>
      );
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden mb-8">
      <div className="border-b border-gray-200 dark:border-gray-700 p-4 flex flex-wrap justify-between items-center gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Eye className="w-5 h-5 mr-2 text-indigo-500" />
          Предпросмотр текста
        </h3>
        
        {/* Селектор режима предпросмотра */}
        <div className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
          <button
            onClick={() => setPreviewMode('desktop')}
            className={`flex items-center px-3 py-1.5 text-xs font-medium rounded-md ${
              previewMode === 'desktop'
                ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
            }`}
            title="Настольный компьютер"
          >
            <MonitorCheck className="w-4 h-4 mr-1" />
            ПК
          </button>
          <button
            onClick={() => setPreviewMode('tablet')}
            className={`flex items-center px-3 py-1.5 text-xs font-medium rounded-md ${
              previewMode === 'tablet'
                ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
            }`}
            title="Планшет"
          >
            <Tablet className="w-4 h-4 mr-1" />
            Планшет
          </button>
          <button
            onClick={() => setPreviewMode('mobile')}
            className={`flex items-center px-3 py-1.5 text-xs font-medium rounded-md ${
              previewMode === 'mobile'
                ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
            }`}
            title="Смартфон"
          >
            <Smartphone className="w-4 h-4 mr-1" />
            Телефон
          </button>
        </div>
        
        {/* Селектор контекста предпросмотра */}
        <div className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
          <button
            onClick={() => setPreviewContext('normal')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md ${
              previewContext === 'normal'
                ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            Обычный
          </button>
          <button
            onClick={() => setPreviewContext('article')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md ${
              previewContext === 'article'
                ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            Статья
          </button>
          <button
            onClick={() => setPreviewContext('ui')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md ${
              previewContext === 'ui'
                ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            Интерфейс
          </button>
          <button
            onClick={() => setPreviewContext('print')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md ${
              previewContext === 'print'
                ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            Печать
          </button>
        </div>
      </div>
      
      {/* Область предпросмотра */}
      <div 
        className="text-preview-panel p-6"
        style={{ 
          backgroundColor: formData.colorScheme === 'dark' ? '#1f2937' : '#f9fafb',
          minHeight: '300px',
          transition: 'all 0.2s ease-in-out',
          overflow: 'auto'
        }}
      >
        <div
          className="preview-container mx-auto transition-all duration-300 ease-in-out"
          style={{
            ...getContainerWidth(),
            backgroundColor: formData.colorScheme === 'dark' ? '#333333' : '#ffffff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            borderRadius: '0.5rem',
            ...getContextStyle()
          }}
        >
          {getPreviewContent()}
        </div>
      </div>
      
      {/* Информационная панель */}
      <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 border-t border-indigo-100 dark:border-indigo-800">
        <div className="flex items-start text-sm">
          <Layers className="w-5 h-5 mr-3 text-indigo-500 mt-0.5" />
          <div>
            <p className="text-indigo-700 dark:text-indigo-300">
              <span className="font-medium">Режим предпросмотра:</span> {previewMode === 'desktop' ? 'Компьютер' : previewMode === 'tablet' ? 'Планшет' : 'Смартфон'} |
              <span className="font-medium"> Контекст:</span> {
                previewContext === 'normal' ? 'Обычный текст' : 
                previewContext === 'article' ? 'Статья' : 
                previewContext === 'ui' ? 'Интерфейс' : 'Печатный документ'
              }
            </p>
            <p className="text-indigo-600 dark:text-indigo-400 text-xs mt-1">
              Размер шрифта: <strong>{getFontSize()}</strong> | 
              Интерлиньяж: <strong>{formData.lineHeight}</strong> | 
              Трекинг: <strong>{formData.letterSpacing}em</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextPreview;